/**
 * Pure presentation rules for the tier-1 graph KPIs.
 *
 * They live outside the JSX because each one is a rule about NOT lying on screen, and a rule you
 * cannot unit-test without a DOM is a rule nobody will keep. Spec: `.agent_docs/graph-kpi-tier1.md`.
 */

import type { GraphKpiUnknownReason, GraphKpiValue, KpiPolarity } from '@snxt/snxt-onto-share';

/**
 * The KPIs whose `value` is an absolute measure, not a ratio in [0, 1]. Everything else is a
 * fraction and renders as a percentage. Kept as data rather than an `if` per KPI so adding a
 * tier-2 KPI does not mean touching the renderer.
 */
const ABSOLUTE_KPIS = new Set<GraphKpiValue['id']>(['freshness', 'evidence_per_instance']);

const ABSOLUTE_UNITS: Partial<Record<GraphKpiValue['id'], string>> = {
  freshness: 'h',
  evidence_per_instance: ' per instance (median)',
};

/** What the user reads instead of a number. Never "0", never "-", never an empty tile. */
const REASON_TEXT: Record<GraphKpiUnknownReason, string> = {
  not_populated: 'no data yet',
  empty_denominator: 'empty denominator',
  schema_feature_absent: 'not computed',
};

export function reasonText(reason: GraphKpiUnknownReason | undefined): string {
  // An absent reason is still not a zero: the honest fallback is the generic wording.
  return reason === undefined ? 'not computed' : REASON_TEXT[reason];
}

/**
 * The headline string of a tile.
 *
 * Rule 1 of the spec: a KPI with `value === null` renders its reason as TEXT. Returning `null` here
 * would let a caller fall back to `?? 0` or `?? '-'`, which is exactly the bug, so this function
 * always returns something readable and never a numeral for a missing value.
 */
export function formatKpiValue(kpi: GraphKpiValue): string {
  if (kpi.value === null) return reasonText(kpi.reason);

  if (ABSOLUTE_KPIS.has(kpi.id)) {
    const unit = ABSOLUTE_UNITS[kpi.id] ?? '';
    return `${kpi.value}${unit}`;
  }
  return `${Math.round(kpi.value * 1000) / 10}%`;
}

/**
 * Rule 4: a ratio shows both of its terms, so 1/1 is distinguishable from 900/900. `null` when the
 * KPI is not a ratio or did not return its terms; the caller then renders nothing rather than
 * inventing a denominator.
 */
export function formatKpiTerms(kpi: GraphKpiValue): string | null {
  if (kpi.value === null) return null;
  if (kpi.numerator === undefined || kpi.denominator === undefined) return null;
  return `${kpi.numerator} / ${kpi.denominator}`;
}

/**
 * Rule 5: `polarity` drives the colour, never the KPI id. `lower-is-better` means a high value is
 * bad, so the scale is inverted rather than duplicated per KPI.
 *
 * `undefined` when there is nothing to colour, which is the case for a missing value (colouring an
 * unknown would suggest it was measured) and for the absolute KPIs, which have no [0, 1] scale and
 * therefore no defensible threshold at this stage.
 */
export type KpiTone = 'good' | 'warning' | 'bad';

export function kpiTone(kpi: GraphKpiValue): KpiTone | undefined {
  if (kpi.value === null) return undefined;
  if (ABSOLUTE_KPIS.has(kpi.id)) return undefined;

  const healthy = kpi.polarity === 'lower-is-better' ? 1 - kpi.value : kpi.value;
  if (healthy >= 0.9) return 'good';
  if (healthy >= 0.6) return 'warning';
  return 'bad';
}

/** Carbon `Tag` type for a tone. Kept next to `kpiTone` so a new tone cannot be forgotten here. */
export function toneTagType(tone: KpiTone | undefined): 'green' | 'teal' | 'red' | 'gray' {
  switch (tone) {
    case 'good':
      return 'green';
    case 'warning':
      return 'teal';
    case 'bad':
      return 'red';
    default:
      return 'gray';
  }
}

/**
 * Rule 3: a tile is clickable only when there is a set to open. `freshness` carries
 * `rowCount === null` by construction (it is a single value, not a predicate over rows), and a
 * KPI with zero offending rows has nothing to show either.
 */
export function hasDrillDown(kpi: GraphKpiValue): boolean {
  return kpi.rowCount !== null && kpi.rowCount > 0;
}

/** Sentence-case a `detail` key (`p90` → `P90`, `single_evidence` → `Single evidence`). */
export function formatDetailKey(key: string): string {
  const spaced = key.replace(/_/g, ' ');
  return spaced.charAt(0).toUpperCase() + spaced.slice(1);
}

/** A drill-down column header from a `detail` key, same casing rule as the tiles. */
export const formatRowColumn = formatDetailKey;

/** Human wording for an invariant verdict. `unknown` must never read as a pass or a failure. */
export function invariantStatusLabel(status: 'satisfied' | 'violated' | 'unknown'): string {
  switch (status) {
    case 'satisfied':
      return 'satisfied';
    case 'violated':
      return 'violated';
    default:
      return 'not measured';
  }
}

export function polarityHint(polarity: KpiPolarity): string {
  return polarity === 'lower-is-better' ? 'lower is better' : 'higher is better';
}
